// src/pages/CountryDetail.tsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";


interface Country {
  id: number;
  name: string;
  created_at: string;
}

interface Dealer {
  id: number;
  company_name: string;
  created_at: string;
  country_id: number;
}

interface Contact {
  id: number;
  name: string;
  location: string;
  affiliation: string;
  contact_information: string;
  created_at: string;
  country_id: number;
  hidden?: boolean;
}

interface Event {
  id: number;
  event_name: string;
  location: string;
  time_of_year: string;
  focus_area: string;
  created_at: string;
  country_id: number;
}

const CountryDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [country, setCountry] = useState<Country | null>(null);
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [events, setEvents] = useState<Event[]>([]);

  // New states for report sections
  const [riskAssessment, setRiskAssessment] = useState<any>(null);
  const [cropAssessment, setCropAssessment] = useState<any>(null);
  const [fieldResearch, setFieldResearch] = useState<any>(null);
  const [additionalNotes, setAdditionalNotes] = useState<any>(null);

  const [activeInfoTab, setActiveInfoTab] = useState("Existing Dealers");
  const [activeDummyTab, setActiveDummyTab] = useState("Risk Assessment");

  // If navigation state sets an active tab (e.g., coming back from an edit page), use it.
  useEffect(() => {
    if (location.state && (location.state as any).activeTab) {
      setActiveInfoTab((location.state as any).activeTab);
    }
  }, [location.state]);

  useEffect(() => {
    // Fetch country details
    fetch(`/api/countries/${id}`)
      .then((res) => res.json())
      .then((data) => setCountry(data))
      .catch((err) => console.error("Error fetching country:", err));

    // Fetch dealers
    fetch(`/api/countries/${id}/dealers`)
      .then((res) => res.json())
      .then((data) => setDealers(data))
      .catch((err) => console.error("Error fetching dealers:", err));

    // Fetch contacts (filter out hidden)
    fetch(`/api/countries/${id}/contacts`)
      .then((res) => res.json())
      .then((data) => setContacts(data.filter((contact: Contact) => !contact.hidden)))
      .catch((err) => console.error("Error fetching contacts:", err));

    // Fetch events
    fetch(`/api/countries/${id}/events`)
      .then((res) => res.json())
      .then((data) => setEvents(data))
      .catch((err) => console.error("Error fetching events:", err));

    // Fetch risk assessment data
    fetch(`/api/countries/${id}/risk-assessment`)
      .then((res) => res.json())
      .then((data) => setRiskAssessment(data))
      .catch((err) => console.error("Error fetching risk assessment:", err));

    // Fetch crop assessment data
    fetch(`/api/countries/${id}/crop-assessment`)
      .then((res) => res.json())
      .then((data) => setCropAssessment(data))
      .catch((err) => console.error("Error fetching crop assessment:", err));

    // Fetch field research data
    fetch(`/api/countries/${id}/field-research`)
      .then((res) => res.json())
      .then((data) => setFieldResearch(data))
      .catch((err) => console.error("Error fetching field research:", err));

    // Fetch additional notes
    fetch(`/api/countries/${id}/additional-notes`)
      .then((res) => res.json())
      .then((data) => setAdditionalNotes(data))
      .catch((err) => console.error("Error fetching additional notes:", err));
  }, [id]);

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Style objects
  const containerStyle: React.CSSProperties = {
    backgroundColor: "#F0F5F0",
    minHeight: "100vh",
    padding: "20px",
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    maxWidth: "800px",
    margin: "30px auto",
    padding: "30px",
  };

  const headerStyle: React.CSSProperties = {
    textAlign: "center",
    color: "#e41e26",
    borderBottom: "2px solid #e41e26",
    paddingBottom: "10px",
    marginBottom: "20px",
    fontSize: "24px",
  };

  const subheaderStyle: React.CSSProperties = {
    textAlign: "center",
    color: "#e41e26",
    marginBottom: "20px",
    fontSize: "20px",
  };

  const sectionHeaderStyle: React.CSSProperties = {
    color: "#e41e26",
    borderBottom: "1px solid #e41e26",
    paddingBottom: "5px",
    marginTop: "15px",
    fontSize: "16px",
  };

  const buttonStyle: React.CSSProperties = {
    padding: "5px 10px",
    backgroundColor: "#e41e26",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  };

  const tabContainerStyle: React.CSSProperties = {
    display: "flex",
    borderBottom: "1px solid #ccc",
    marginBottom: "20px",
  };

  const tabStyle: React.CSSProperties = {
    flex: 1,
    padding: "10px",
    textAlign: "center",
    cursor: "pointer",
    backgroundColor: "#f0f0f0",
  };

  const activeTabStyle: React.CSSProperties = {
    ...tabStyle,
    backgroundColor: "#e41e26",
    color: "#fff",
    fontWeight: "bold",
  };

  const breadcrumbLinkStyle: React.CSSProperties = {
    textDecoration: "none",
    color: "#e41e26",
    fontWeight: "bold",
  };

  // Define two separate fixed height containers:
  const infoTabContentStyle: React.CSSProperties = {
    minHeight: "300px",
    maxHeight: "300px",
    overflowY: "auto",
  };

  const dummyTabContentStyle: React.CSSProperties = {
    minHeight: "800px",
    maxHeight: "800px",
    overflowY: "auto",
  };

  // Breadcrumb items
  const breadcrumbItems = [
    { label: "Home", path: "/" },
    { label: country ? country.name : "Country Detail", path: `/country/${id}` },
  ];

  // Report sections display components
  const riskAssessmentDisplay = (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h2 style={subheaderStyle}>Risk Assessment</h2>
        <button style={buttonStyle} onClick={() => navigate(`/country/${id}/edit-risk-assessment`)}>
          Edit
        </button>
      </div>
      {riskAssessment ? (
        <>
          <div>
  <h3 style={sectionHeaderStyle}>Economic Stability</h3>
 <div style={{ marginBottom: "10px" }}>
  <strong>GDP:</strong>{" "}
  <span>
    {riskAssessment.gdp != null
      ? (() => {
          const n = Number(riskAssessment.gdp);
          if (n >= 1e12) {
            return `$${(n / 1e12).toFixed(2)} Trillion`;
          }
          if (n >= 1e9) {
            return `$${(n / 1e9).toFixed(2)} Billion`;
          }
          if (n >= 1e6) {
            return `$${(n / 1e6).toFixed(2)} Million`;
          }
          // fallback to full dollar amount
          return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD"
          }).format(n);
        })()
      : "N/A"}
  </span>
</div>

  <div style={{ marginBottom: "10px" }}>
    <strong>GDP Growth Rate:</strong>{" "}
    <span>
      {riskAssessment.gdp_growth_rate != null
        ? `${riskAssessment.gdp_growth_rate}%`
        : "N/A"}
    </span>
  </div>
  <div style={{ marginBottom: "10px" }}>
    <strong>Inflation Rate:</strong>{" "}
    <span>
      {riskAssessment.inflation_rate != null
        ? `${riskAssessment.inflation_rate}%`
        : "N/A"}
    </span>
  </div>
  <div style={{ marginBottom: "10px" }}>
    <strong>Unemployment Rate:</strong>{" "}
    <span>
      {riskAssessment.unemployment_rate != null
        ? `${riskAssessment.unemployment_rate}%`
        : "N/A"}
    </span>
  </div>
  <div style={{ marginBottom: "10px" }}>
    <strong>Public Debt as % of GDP:</strong>{" "}
    <span>
      {riskAssessment.public_debt_percent != null
        ? `${riskAssessment.public_debt_percent}%`
        : "N/A"}
    </span>
  </div>
</div>

<div>
  <h3 style={sectionHeaderStyle}>Political Environment</h3>
  <div style={{ marginBottom: "10px" }}>
    <strong>Political Stability Index:</strong>{" "}
    <span>
      {(() => {
        const raw = riskAssessment.political_stability_index;
        const num = Number(raw);
        return !isNaN(num) ? num.toFixed(2) : raw || "N/A";
      })()}
    </span>
  </div>
  <div style={{ marginBottom: "10px" }}>
    <strong>Recent Political Events:</strong>{" "}
    <span>{riskAssessment.recent_political_events || "N/A"}</span>
  </div>
  <div style={{ marginBottom: "10px" }}>
    <strong>Risk of Violence:</strong>{" "}
    <span>{riskAssessment.risk_of_armed_conflict || "N/A"}</span>
  </div>
</div>


<div>
  <h3 style={sectionHeaderStyle}>Environmental Factors Affecting Agriculture</h3>
  <div style={{ marginBottom: "10px" }}>
    <strong>Natural Disasters:</strong>{" "}
    <span>{riskAssessment.natural_disasters || "N/A"}</span>
  </div>
  <div style={{ marginBottom: "10px" }}>
    <strong>Agricultural Land Impacted:</strong>{" "}
    <span>
      {riskAssessment.agricultural_land_impact != null
        ? `${riskAssessment.agricultural_land_impact}%`
        : "N/A"}
    </span>
  </div>
  <div style={{ marginBottom: "10px" }}>
    <strong>Weather Vulnerability Index:</strong>{" "}
    <span>{riskAssessment.weather_vulnerability_index ?? "N/A"}</span>
  </div>
  <div style={{ marginBottom: "10px" }}>
    <strong>Frost Impact:</strong>{" "}
    <span>{riskAssessment.frost_impact || "N/A"}</span>
  </div>
  <div style={{ marginBottom: "10px" }}>
    <strong>Recent Frost Events:</strong>{" "}
    <span>{riskAssessment.frost_events || "N/A"}</span>
  </div>
</div>

        </>
      ) : (
        <p>Loading risk assessment data...</p>
      )}
    </div>
  );

const cropAssessmentDisplay = (
  <div>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
      <h2 style={subheaderStyle}>Crop Assessment</h2>
      <button style={buttonStyle} onClick={() => navigate(`/country/${id}/edit-crop-assessment`)}>
        Edit
      </button>
    </div>
    {cropAssessment ? (
      <>
        <div>
          <h3 style={sectionHeaderStyle}>Relevant Crops for Wind Machines</h3>
          <div style={{ marginBottom: "10px" }}>
            <strong>Crop:</strong> <span>{cropAssessment.wind_crop || "N/A"}</span>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <strong>Annual Production Volume:</strong>{" "}
            <span>
  {cropAssessment.wind_annual_production_volume != null
    ? `${new Intl.NumberFormat("en-US").format(cropAssessment.wind_annual_production_volume)} metric tons`
    : "N/A"}
</span>

          </div>
          <div style={{ marginBottom: "10px" }}>
            <strong>Global Production Percentage:</strong>{" "}
            <span>
              {cropAssessment.wind_global_production_percentage != null
                ? `${cropAssessment.wind_global_production_percentage}%`
                : "N/A"}
            </span>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <strong>Acreage:</strong>{" "}
            <span>
              {cropAssessment.wind_acreage_size != null
                ? new Intl.NumberFormat("en-US").format(cropAssessment.wind_acreage_size)
                : "N/A"}
            </span>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <strong>Crop Returns:</strong>{" "}
            <span>
              {cropAssessment.wind_crop_returns != null
                ? `${cropAssessment.wind_crop_returns}%`
                : "N/A"}
            </span>
          </div>
        </div>

        <div>
          <h3 style={sectionHeaderStyle}>Relevant Crops for Tree Shakers</h3>
          <div style={{ marginBottom: "10px" }}>
            <strong>Crop:</strong> <span>{cropAssessment.tree_crop || "N/A"}</span>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <strong>Annual Production Volume:</strong>{" "}
            <span>
    {cropAssessment.tree_annual_production_volume != null
      ? `${new Intl.NumberFormat("en-US").format(
          cropAssessment.tree_annual_production_volume
        )} metric tons`
      : "N/A"}
  </span>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <strong>Global Production Percentage:</strong>{" "}
            <span>
              {cropAssessment.tree_global_production_percentage != null
                ? `${cropAssessment.tree_global_production_percentage}%`
                : "N/A"}
            </span>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <strong>Acreage:</strong>{" "}
            <span>
              {cropAssessment.tree_acreage_size != null
                ? new Intl.NumberFormat("en-US").format(cropAssessment.tree_acreage_size)
                : "N/A"}
            </span>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <strong>Crop Returns:</strong>{" "}
            <span>
              {cropAssessment.tree_crop_returns != null
                ? `${cropAssessment.tree_crop_returns}%`
                : "N/A"}
            </span>
          </div>
        </div>
      </>
    ) : (
      <p>Loading crop assessment data...</p>
    )}
  </div>
);


  const fieldResearchDisplay = (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h2 style={subheaderStyle}>Field Research</h2>
        <button style={buttonStyle} onClick={() => navigate(`/country/${id}/edit-field-research`)}>
          Edit
        </button>
      </div>
      {fieldResearch ? (
        <>
          <div>
            <h3 style={sectionHeaderStyle}>Current Practices</h3>
            <div style={{ marginBottom: "10px" }}>
              <strong>Frost Mitigation:</strong> <span>{fieldResearch.frost_mitigation || "N/A"}</span>
            </div>
            <div style={{ marginBottom: "10px" }}>
              <strong>Noise Ordinances:</strong> <span>{fieldResearch.noise_ordinances || "N/A"}</span>
            </div>
            <div style={{ marginBottom: "10px" }}>
              <strong>Fuel Types:</strong> <span>{fieldResearch.fuel_types || "N/A"}</span>
            </div>
            <div style={{ marginBottom: "10px" }}>
              <strong>Crop Insurance:</strong> <span>{fieldResearch.crop_insurance || "N/A"}</span>
            </div>
            <div style={{ marginBottom: "10px" }}>
              <strong>Government Subsidies:</strong> <span>{fieldResearch.government_subsidies || "N/A"}</span>
            </div>
          </div>
                   <div style={{ marginBottom: "10px" }}>
           <strong>Existing Competitors:</strong>{" "}
           <span>{fieldResearch.existing_competitors || "N/A"}</span>
         </div>
         <div style={{ marginBottom: "10px" }}>
           <strong>Market Share:</strong>{" "}
           <span>
             {fieldResearch.market_share != null
               ? `${fieldResearch.market_share}%`
               : "N/A"}
           </span>
         </div>
          <div>
            <h3 style={sectionHeaderStyle}>Customer Insights</h3>
            <div style={{ marginBottom: "10px" }}>
              <strong>Reasons for Choosing Our Products:</strong> <span>{fieldResearch.reasons_for_choosing || "N/A"}</span>
            </div>
            <div style={{ marginBottom: "10px" }}>
              <strong>Reasons Against Choosing Our Products:</strong> <span>{fieldResearch.reasons_against_choosing || "N/A"}</span>
            </div>
            <div style={{ marginBottom: "10px" }}>
              <strong>Feedback on Competitors:</strong>
              <div style={{ marginLeft: "20px" }}>
                <div style={{ marginBottom: "5px" }}>
                  <em>Positive Aspects:</em> <span>{fieldResearch.competitor_feedback_positive || "N/A"}</span>
                </div>
                <div>
                  <em>Negative Aspects:</em> <span>{fieldResearch.competitor_feedback_negative || "N/A"}</span>
                </div>
              </div>
            </div>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <strong>Sources:</strong> <span>{fieldResearch.sources || "N/A"}</span>
          </div>
        </>
      ) : (
        <p>Loading field research data...</p>
      )}
    </div>
  );

  const additionalNotesDisplay = (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h2 style={subheaderStyle}>Additional Notes</h2>
        <button style={buttonStyle} onClick={() => navigate(`/country/${id}/edit-additional-notes`)}>
          Edit
        </button>
      </div>
      {additionalNotes ? (
        <div style={{ marginBottom: "10px" }}>
          <strong>Additional Information:</strong> <span>{additionalNotes.notes || "N/A"}</span>
        </div>
      ) : (
        <p>Loading additional notes...</p>
      )}
    </div>
  );

  // Dummy tabs array for report sections using read-only displays
  const dummyTabs = [
    { label: "Risk Assessment", content: riskAssessmentDisplay },
    { label: "Crop Assessment", content: cropAssessmentDisplay },
    { label: "Field Research", content: fieldResearchDisplay },
    { label: "Additional Notes", content: additionalNotesDisplay },
  ];

  // Info tabs content for Dealers, Contacts, and Events
  const infoTabs = [
    {
      label: "Existing Dealers",
      content: (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h2 style={subheaderStyle}>Existing Dealers</h2>
            <button style={buttonStyle} onClick={() => navigate(`/country/${id}/add-dealer`)}>
              Edit
            </button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", cursor: "pointer" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Dealer</th>
                <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "right", color: "#e41e26" }}>Added</th>
              </tr>
            </thead>
            <tbody>
  {dealers.map((dealer) => (
    <tr key={dealer.id}>
      <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>
        <button
          style={buttonStyle}
          onClick={() => navigate(`/dealer/${dealer.id}`)}
        >
          {dealer.company_name}
        </button>
      </td>
      <td
        style={{
          padding: "8px",
          borderBottom: "1px solid #ccc",
          textAlign: "right",
        }}
      >
        {formatDate(dealer.created_at)}
      </td>
    </tr>
  ))}
</tbody>
          </table>
        </div>
      ),
    },
    {
      label: "Contacts",
      content: (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h2 style={subheaderStyle}>Contacts</h2>
            <button style={buttonStyle} onClick={() => navigate(`/country/${id}/edit-contact`)}>
              Edit
            </button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", cursor: "default" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Name</th>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Location</th>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Affiliation</th>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Contact Info</th>
                <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "right", color: "#e41e26" }}>Added</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact) => (
                <tr key={contact.id}>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>{contact.name}</td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>{contact.location}</td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>{contact.affiliation}</td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>{contact.contact_information}</td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "right" }}>
                    {formatDate(contact.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ),
    },
    {
      label: "Events",
      content: (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h2 style={subheaderStyle}>Events</h2>
            <button style={buttonStyle} onClick={() => navigate(`/country/${id}/edit-event`)}>
              Edit
            </button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", cursor: "default" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Event Name</th>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Location</th>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Time of Year</th>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ccc", color: "#e41e26" }}>Focus Area</th>
                <th style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "right", color: "#e41e26" }}>Added</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id}>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>{event.event_name}</td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>{event.location}</td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>{event.time_of_year}</td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>{event.focus_area}</td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ccc", textAlign: "right" }}>
                    {formatDate(event.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ),
    },
  ];

  return (
    <div style={containerStyle}>
      <Navbar />
      {country ? (
        <>
          {/* Country Card with Breadcrumbs */}
          <div style={cardStyle}>
            <div style={{ marginBottom: "10px" }}>
              <nav aria-label="breadcrumb">
                {breadcrumbItems.map((item, index) => (
                  <span key={item.path}>
                    <Link to={item.path} style={breadcrumbLinkStyle}>
                      {item.label}
                    </Link>
                    {index < breadcrumbItems.length - 1 && " / "}
                  </span>
                ))}
              </nav>
            </div>
            <h1 style={headerStyle}>{country.name}</h1>
            
          </div>

          {/* Tabbed group for Existing Dealers, Contacts, and Events */}
          <div style={cardStyle}>
            <div style={tabContainerStyle}>
              {infoTabs.map((tab) => (
                <div
                  key={tab.label}
                  style={activeInfoTab === tab.label ? activeTabStyle : tabStyle}
                  onClick={() => setActiveInfoTab(tab.label)}
                >
                  {tab.label}
                </div>
              ))}
            </div>
            {/* Use infoTabContentStyle for this group */}
            <div style={infoTabContentStyle}>
              {infoTabs.find((tab) => tab.label === activeInfoTab)?.content}
            </div>
          </div>

          {/* Report Sections rendered as read-only displays */}
          <div style={cardStyle}>
            <div style={tabContainerStyle}>
              {dummyTabs.map((tab) => (
                <div
                  key={tab.label}
                  style={activeDummyTab === tab.label ? activeTabStyle : tabStyle}
                  onClick={() => setActiveDummyTab(tab.label)}
                >
                  {tab.label}
                </div>
              ))}
            </div>
            {/* Use dummyTabContentStyle for this group */}
            <div style={dummyTabContentStyle}>
              {dummyTabs.find((tab) => tab.label === activeDummyTab)?.content}
            </div>
          </div>
        </>
      ) : (
        <p style={{ textAlign: "center" }}>Loading country details...</p>
      )}
    </div>
  );
};

export default CountryDetail;
